import React, { useState } from 'react';
import { usePlan } from '../context/PlanContext';
import UpgradeModal from './UpgradeModal';
import { Lock, AlertCircle } from 'lucide-react';

/**
 * Wraps a feature section — shows upgrade prompt if feature/limit is blocked.
 * Usage: <PlanGate feature="ai_assistant"> or <PlanGate resource="contacts">
 */
export default function PlanGate({ feature, resource, children, inline }) {
    const { checkFeature, checkLimit, planLabel, isConnectionError, retry, planUnknown } = usePlan();
    const [showUpgrade, setShowUpgrade] = useState(false);

    const featureBlocked = feature && !checkFeature(feature);
    const limitBlocked = resource && !checkLimit(resource).allowed;
    const blocked = featureBlocked || limitBlocked;

    // "Not loaded yet" is not "not entitled". `checkFeature` fails closed, so
    // during the plan fetch every gate reads as blocked — rendering the upgrade
    // prompt then would flash "Upgrade to unlock" at a customer who already
    // pays for the feature, on every page load.
    if (planUnknown && !isConnectionError) {
        return inline ? null : (
            <div
                className="rounded-xl border border-dashed border-stone-200 bg-stone-50/50 p-8 text-center"
                data-testid="plan-gate-loading"
                aria-busy="true"
            >
                <div className="w-6 h-6 border-2 border-stone-300 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
        );
    }

    if (!blocked) return <>{children}</>;

    // When the block is caused by a transient connection error (API 500 /
    // timeout / network error) rather than a real plan limit, show a
    // retry prompt instead of the upgrade UI. This avoids confusing users
    // into thinking they need to upgrade when the real issue is a
    // temporary server failure.
    if (isConnectionError) {
        if (inline) {
            return (
                <button
                    onClick={retry}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-xs font-medium hover:bg-amber-100 transition-colors"
                    data-testid="plan-gate-connection-inline"
                >
                    <AlertCircle className="w-3 h-3" />
                    Connection error — retry
                </button>
            );
        }

        return (
            <div
                className="relative rounded-xl border border-dashed border-amber-300 bg-amber-50/50 p-8 text-center cursor-pointer hover:bg-amber-50 transition-colors"
                onClick={retry}
                data-testid="plan-gate-connection"
            >
                <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                <h3 className="font-semibold text-stone-700 mb-1">
                    Connection error
                </h3>
                <p className="text-sm text-stone-500">
                    We couldn't reach the server to verify your plan. Retrying automatically — click to retry now.
                </p>
            </div>
        );
    }

    if (inline) {
        return (
            <>
                <button
                    onClick={() => setShowUpgrade(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-100 text-stone-500 text-xs font-medium hover:bg-stone-200 transition-colors"
                    data-testid="plan-gate-inline"
                >
                    <Lock className="w-3 h-3" />
                    {planLabel} plan — Upgrade to unlock
                </button>
                <UpgradeModal
                    open={showUpgrade}
                    onClose={() => setShowUpgrade(false)}
                    triggerFeature={feature}
                    triggerResource={resource}
                />
            </>
        );
    }

    return (
        <>
            <div
                className="relative rounded-xl border border-dashed border-stone-300 bg-stone-50/50 p-8 text-center cursor-pointer hover:bg-stone-50 transition-colors"
                onClick={() => setShowUpgrade(true)}
                data-testid="plan-gate-block"
            >
                <Lock className="w-8 h-8 text-stone-400 mx-auto mb-3" />
                <h3 className="font-semibold text-stone-700 mb-1">
                    {feature ? `${feature.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}` : resource?.replace(/_/g, ' ')} — {planLabel} Plan
                </h3>
                <p className="text-sm text-stone-500">
                    {featureBlocked
                        ? 'This feature is not available on your current plan. Click to see upgrade options.'
                        : `You've reached the ${resource?.replace(/_/g, ' ')} limit on your current plan. Click to see upgrade options.`}
                </p>
            </div>
            <UpgradeModal
                open={showUpgrade}
                onClose={() => setShowUpgrade(false)}
                triggerFeature={feature}
                triggerResource={resource}
            />
        </>
    );
}
